import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import { ShieldCheck, PaperPlaneTilt, FlowArrow, Check, X } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

export default function ApprovalsQueuePage() {
  const nav = useNavigate();
  const [data, setData] = useState({ campaigns: [], workflows: [], is_admin: false });
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/approvals/pending");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const approve = async (kind, id) => {
    const notes = window.prompt("Approval notes (optional):", "") || "";
    try {
      await api.post(`/marketing/${kind}/${id}/approve`, { notes });
      toast.success("Approved");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const reject = async (kind, id) => {
    const notes = window.prompt("Reason for rejection (required):", "");
    if (!notes) return;
    try {
      await api.post(`/marketing/${kind}/${id}/reject`, { notes });
      toast.success("Rejected");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const totalPending = data.campaigns.length + data.workflows.length;

  return (
    <MarketingShell>
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck size={22} className="text-[#0055FF]" />
        <div>
          <h2 className="text-lg font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {data.is_admin ? "Approval queue" : "My pending approvals"}
          </h2>
          <p className="text-xs text-[#525252]">
            {data.is_admin
              ? "Every campaign and workflow below is waiting on a platform admin. Review the content, then approve or send it back to the creator."
              : "Campaigns and workflows you've submitted that are waiting on a platform admin to review."}
          </p>
        </div>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-[#FFFBEB] text-[#92400E] px-2 py-1" data-testid="approvals-count">
          {totalPending} pending
        </span>
      </div>

      {loading ? (
        <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
      ) : totalPending === 0 ? (
        <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-testid="approvals-empty">
          <ShieldCheck size={36} className="text-[#16A34A] mx-auto mb-2" />
          <p className="text-sm font-bold text-[#0A0A0A]">All clear</p>
          <p className="text-xs text-[#525252] mt-1">
            {data.is_admin ? "No campaigns or workflows are waiting for approval."
                           : "You have no pending submissions."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.campaigns.length > 0 && (
            <QueueSection
              title="Campaigns"
              Icon={PaperPlaneTilt}
              rows={data.campaigns}
              kind="campaigns"
              isAdmin={data.is_admin}
              onOpen={(id) => nav(`/marketing/campaigns/${id}/edit`)}
              onApprove={approve}
              onReject={reject}
              subtitle={(r) => r.subject_line}
            />
          )}
          {data.workflows.length > 0 && (
            <QueueSection
              title="Workflows"
              Icon={FlowArrow}
              rows={data.workflows}
              kind="workflows"
              isAdmin={data.is_admin}
              onOpen={(id) => nav(`/marketing/workflows/${id}`)}
              onApprove={approve}
              onReject={reject}
              subtitle={(r) => `Trigger: ${r.trigger_type}`}
            />
          )}
        </div>
      )}
    </MarketingShell>
  );
}

function QueueSection({ title, Icon, rows, kind, isAdmin, onOpen, onApprove, onReject, subtitle }) {
  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid={`queue-section-${kind}`}>
      <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center gap-2">
        <Icon size={14} className="text-[#525252]" />
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">{title}</h3>
        <span className="text-[10px] text-[#A3A3A3]">· {rows.length}</span>
      </div>
      <ul>
        {rows.map((r) => (
          <li key={r.id} className="border-t border-[#F5F5F5] first:border-t-0 px-4 py-3 flex items-center gap-3 hover:bg-[#FAFAFA]"
              data-testid={`queue-row-${r.id}`}>
            <button onClick={() => onOpen(r.id)} className="flex-1 text-left">
              <p className="text-sm font-bold text-[#0A0A0A]">{r.name}</p>
              <p className="text-[11px] text-[#525252] mt-0.5">
                {subtitle(r) || "—"}
                {r.updated_at && <span className="ml-2 text-[#A3A3A3]">· updated {formatLocalDate(r.updated_at)}</span>}
              </p>
            </button>
            <button onClick={() => onOpen(r.id)}
                    className="h-8 px-3 text-[11px] font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF]"
                    data-testid={`queue-open-${r.id}`}>
              Open
            </button>
            {isAdmin && (
              <>
                <button onClick={() => onApprove(kind, r.id)}
                        className="h-8 px-3 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#15803D] flex items-center gap-1"
                        data-testid={`queue-approve-${r.id}`}>
                  <Check size={11} weight="bold" />Approve
                </button>
                <button onClick={() => onReject(kind, r.id)}
                        className="h-8 px-3 border border-[#DC2626] text-[#DC2626] text-[11px] font-bold uppercase tracking-wider hover:bg-[#FEF2F2] flex items-center gap-1"
                        data-testid={`queue-reject-${r.id}`}>
                  <X size={11} weight="bold" />Reject
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
